import { Injectable } from '@angular/core';
import { CostTypeHelper } from '@core/helpers/cost-type.helper';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { Estimate } from '@interfaces/Estimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { Structure } from '@interfaces/Structure';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { EstimateService } from './estimate.service';

@Injectable({
  providedIn: 'root'
})
export class EstimateStructureLinksService {
  public constructor(private estimateService: EstimateService, private logger: NGXLogger) {}

  public async disassociateDeptEstimFromStructure(parentStructure: Structure, departmentEstimateId: string) {
    await this.estimateService.deleteEstimateLinesByStructByDeptEstim(parentStructure.id, departmentEstimateId);
  }

  public linkDeptToEstimate(estimate: Estimate, departmentId: string) {
    return this.estimateService.createDepartmentEstimate(estimate.id, departmentId, false);
  }

  public async associateDeptEstimToStructure(estimate: Estimate, structure: Structure, departmentEstimateId: string) {
    // get existing estimate lines for structure and department-estimate
    const existingEstimLines = await firstValueFrom(
      this.estimateService.getEstimateLinesByStructByDeptEstim(structure.id, departmentEstimateId)
    );

    // if lines already exist, return
    if (existingEstimLines.length !== 0) {
      return;
    }

    await this.createEstimateLineBasedOnStructure(structure, estimate, departmentEstimateId);
  }

  public async createEstimateLineBasedOnStructure(
    structure: Structure,
    estimate: Estimate,
    departmentEstimateId: string,
    saveChanges = true
  ) {
    if (!structure) {
      throw Error(`There was no specified structure in createEstimateLineBasedOnStructure`);
    }
    const isByCategory = structure.categoryGroup && estimate.budgetType === BudgetTypeEnum.Detailed;
    let newEstimateLines: EstimateLine[] = [];

    const found = structure.estimateLines.find(
      el => el.structureId === structure.id && el.departmentEstimateId === departmentEstimateId
    );
    if (found) {
      this.logger.info(
        `Found an existing EstimateLine with structureId:${structure.id} and departmentEstimateId:${departmentEstimateId} `
      );
      this.logger.log('continuing');
      return;
    }

    newEstimateLines.push(this.estimateService.createEstimateLine(structure.id, departmentEstimateId));
    // create estimate lines
    if (isByCategory) {
      newEstimateLines = await this.createEstimateLinesByCategory(structure, departmentEstimateId);
    }

    if (saveChanges) {
      await this.estimateService.saveChangesAsync();
    }
  }

  /**
   * Creates all the estimate lines by category
   * for a given structure and department
   * when the estimate's budget type is detailed
   * @param structure
   * @param departmentEstimateId
   */
  private async createEstimateLinesByCategory(
    structure: Structure,
    departmentEstimateId: string
  ): Promise<EstimateLine[]> {
    const newEstimateLines: EstimateLine[] = [];

    // get categories for the structure's category group code
    const categories = await firstValueFrom(
      this.estimateService.getCategoriesByGroupCode(structure.categoryGroup.code)
    );

    const { budgetTypeTime, budgetTypeExpense, budgetTypeInternalFee } = this.estimateService.currentEstimate;

    // create estimate lines for time categories
    if (budgetTypeTime === BudgetTypeEnum.Detailed) {
      for (const cat of categories.filter(c => CostTypeHelper.isTime(c.costType))) {
        newEstimateLines.push(this.estimateService.createEstimateLine(structure.id, departmentEstimateId, cat.id));
      }
    }

    // create estimate lines for expense categories
    if (budgetTypeExpense === BudgetTypeEnum.Detailed) {
      for (const cat of categories.filter(c => CostTypeHelper.isExpense(c.costType))) {
        newEstimateLines.push(this.estimateService.createEstimateLine(structure.id, departmentEstimateId, cat.id));
      }
    }
    // create estimate lines for internalfees categories
    if (budgetTypeInternalFee === BudgetTypeEnum.Detailed) {
      for (const cat of categories.filter(c => CostTypeHelper.isInternalFee(c.costType))) {
        newEstimateLines.push(this.estimateService.createEstimateLine(structure.id, departmentEstimateId, cat.id));
      }
    }
    return newEstimateLines;
  }
}
